import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { DatabaseBackup, Play, Loader2, RotateCcw, ShieldAlert, Info, Cloud, CloudOff, CheckCircle2, XCircle, Lock, AlertTriangle, ImageOff, Clock, ShieldCheck, Image } from "lucide-react";
import { ScaleHealthCard } from "@/components/ScaleHealthCard";
import { ServerDiskCard } from "@/components/ServerDiskCard";
import { ServerCopiesCard } from "@/components/ServerCopiesCard";

const TONES = {
  ok:      { wrap: "border-emerald-200 bg-emerald-50", chip: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-500", text: "text-emerald-800" },
  warn:    { wrap: "border-amber-200 bg-amber-50", chip: "bg-amber-100 text-amber-700", dot: "bg-amber-500", text: "text-amber-800" },
  bad:     { wrap: "border-red-200 bg-red-50", chip: "bg-red-100 text-[#B01E28]", dot: "bg-[#B01E28]", text: "text-[#B01E28]" },
  neutral: { wrap: "border-slate-200 bg-white", chip: "bg-slate-100 text-slate-500", dot: "bg-slate-300", text: "text-slate-600" },
};

function StatusTile({ tone, icon: Icon, title, headline, detail, testid }) {
  const t = TONES[tone] || TONES.neutral;
  return (
    <div data-testid={testid} data-tone={tone} className={`rounded-xl border ${t.wrap} p-4 flex flex-col gap-2`}>
      <div className="flex items-center gap-2">
        <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${t.chip}`}><Icon className="w-4 h-4" /></span>
        <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{title}</span>
        <span className={`ml-auto w-2.5 h-2.5 rounded-full ${t.dot}`} />
      </div>
      <div className={`text-sm font-semibold leading-snug ${t.text}`}>{headline}</div>
      {detail && <div className="text-xs text-slate-500 leading-snug">{detail}</div>}
    </div>
  );
}

export default function BackupsPage() {
  const { has } = useAuth();
  const [info, setInfo] = useState({ backups: [], collections: [] });
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [offsite, setOffsite] = useState(null);
  const [offRunning, setOffRunning] = useState(false);
  const [dlBusy, setDlBusy] = useState(false);
  const [protection, setProtection] = useState(null);
  const [photoHealth, setPhotoHealth] = useState(null);
  const [scale, setScale] = useState(null);
  const [drillBusy, setDrillBusy] = useState(false);
  const [disk, setDisk] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [drill, setDrill] = useState(null);

  const runDrill = async () => {
    if (!window.confirm("Verify your latest off-site backup?\n\nThis DOWNLOADS and DECRYPTS the real backup and checks it on-disk — it counts your donors + all collections, confirms every collection decodes, and opens a photo. It writes NOTHING and touches NO database (safe on your live-DB-only account). May take up to a minute.")) return;
    setDrillBusy(true); setDrill(null);
    try {
      const { data } = await api.post("/admin/backups/verify", null, { timeout: 300000 });
      setDrill(data);
      if (data.ok) toast.success(`Backup VERIFIED — ${data.donors} donors, ${data.collections} collections, all readable.`);
      else toast.error(`Backup verification FAILED: ${data.error || "see details"}`);
      loadOffsite();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Verification failed to run."); }
    finally { setDrillBusy(false); }
  };

  const [verifSchedBusy, setVerifSchedBusy] = useState(false);
  const [staleDaysBusy, setStaleDaysBusy] = useState(false);
  const saveVerifyStaleDays = async (val) => {
    const days = parseInt(val, 10);
    if (!Number.isInteger(days) || days < 1 || days > 90) { toast.error("Enter a whole number of days between 1 and 90."); loadOffsite(); return; }
    if (days === (offsite?.verify_stale_days ?? 7)) return;
    setStaleDaysBusy(true);
    try {
      await api.post("/admin/backups/verify-stale-days", { days });
      toast.success(`Warning threshold set to ${days} day${days === 1 ? "" : "s"}.`);
      loadOffsite();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update the threshold."); }
    finally { setStaleDaysBusy(false); }
  };
  const [landingHoursBusy, setLandingHoursBusy] = useState(false);
  const saveLandingStaleHours = async (val) => {
    const hours = parseInt(val, 10);
    if (!Number.isInteger(hours) || hours < 1 || hours > 168) { toast.error("Enter a whole number of hours between 1 and 168."); loadOffsite(); return; }
    if (hours === (offsite?.landing_stale_hours ?? 26)) return;
    setLandingHoursBusy(true);
    try {
      await api.post("/admin/backups/landing-stale-hours", { hours });
      toast.success(`Landing warning threshold set to ${hours}h.`);
      loadOffsite();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update the threshold."); }
    finally { setLandingHoursBusy(false); }
  };
  const toggleVerifySchedule = async (enabled) => {
    const msg = enabled
      ? "Turn ON monthly auto-verify?\n\nOn the 1st of each month it will run the read-only backup check and email a PASS/FAIL summary to your critical-alert recipients (a FAIL alerts loudly, like a backup failure)."
      : "Turn OFF monthly auto-verify?\n\nYou'll stop getting the automatic monthly proof that your backups are restorable.";
    if (!window.confirm(msg)) return;
    setVerifSchedBusy(true);
    try {
      await api.post("/admin/backups/verify-schedule", { enabled });
      toast.success(enabled ? "Monthly auto-verify turned ON — you'll get an email if a check ever fails." : "Monthly auto-verify turned OFF.");
      loadOffsite();
    } catch (e) { toast.error("Could not change the schedule."); }
    finally { setVerifSchedBusy(false); }
  };

  const canMaintainPhotos = has("manage_users");
  const [orphanBusy, setOrphanBusy] = useState(false);
  const [orphanResult, setOrphanResult] = useState(null);
  const scanOrphans = async (dryRun) => {
    if (!dryRun && !window.confirm("Archive all photo records whose image files are missing? They are ARCHIVED (retained + logged + reversible), never deleted. This clears the blanks left by pre-persistent-storage uploads.")) return;
    setOrphanBusy(true);
    try {
      const { data } = await api.post("/admin/photos/archive-orphans", null, { params: { dry_run: dryRun } });
      setOrphanResult(data);
      if (dryRun) toast.message(`${data.archived} orphaned photo(s) found`, { description: data.archived ? "Click Archive to clear them." : "Nothing to clean up." });
      else toast.success(`Archived ${data.archived} orphaned photo(s)`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setOrphanBusy(false); }
  };

  const [restoreOpen, setRestoreOpen] = useState(false);
  const [rBackup, setRBackup] = useState("");
  const [rColl, setRColl] = useState("");
  const [rRecords, setRRecords] = useState([]);
  const [rRecord, setRRecord] = useState("");

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/backups"); setInfo(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  const loadOffsite = async () => { try { const { data } = await api.get("/admin/backups/offsite"); setOffsite(data); } catch { /* */ } };
  const loadProtection = async () => { try { const { data } = await api.get("/admin/backups/protection"); setProtection(data); } catch { /* */ } };
  const loadPhotoHealth = async () => { try { const { data } = await api.get("/admin/photos/storage-health"); setPhotoHealth(data); } catch { /* */ } };
  const loadScale = async () => { try { const { data } = await api.get("/admin/scale-health"); setScale(data); } catch { /* */ } };
  const loadDisk = async () => { try { const { data } = await api.get("/admin/backups/disk"); setDisk(data); } catch { /* */ } };
  const loadSchedule = async () => { try { const { data } = await api.get("/admin/backups/schedule"); setSchedule(data); } catch { /* */ } };
  useEffect(() => { load(); loadOffsite(); loadPhotoHealth(); loadProtection(); loadScale(); loadDisk(); loadSchedule(); }, []);

  const downloadLatest = async () => {
    setDlBusy(true);
    try {
      const res = await api.get("/admin/backups/offsite/download-latest", { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const name = m ? m[1] : "fas-backup.tar.gz.enc";
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = name; document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
      toast.success("Encrypted backup downloaded — decrypt locally with your passphrase.");
    } catch (e) { toast.error("Download failed — check that an off-site backup exists."); }
    finally { setDlBusy(false); }
  };

  const runOffsite = async () => {
    setOffRunning(true);
    try { const { data } = await api.post("/admin/backups/offsite/run"); if (data.status === "ok") toast.success(`Off-site backup uploaded (${(data.size_bytes / 1e6).toFixed(2)} MB)`); else toast.error(`Off-site backup ${data.status}: ${data.error || ""}`); loadOffsite(); loadDisk(); }
    catch (e) {
      // 409 = a backup is already running. That is not an error worth alarming about.
      if (e.response?.status === 409) toast.info(formatApiErrorDetail(e.response?.data?.detail));
      else toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
    finally { setOffRunning(false); }
  };

  const runBackup = async () => {
    setRunning(true);
    try { await api.post("/admin/backups/run"); toast.success("Backup written to disk"); load(); loadDisk(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setRunning(false); }
  };

  const loadRecords = async (backup, coll) => {
    if (!backup || !coll) return;
    try { const { data } = await api.get(`/admin/backups/${backup}/records`, { params: { collection: coll } }); setRRecords(data.records); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const doRestore = async () => {
    if (!rBackup || !rColl || !rRecord) { toast.error("Pick a backup, collection and record."); return; }
    try { await api.post("/admin/backups/restore", { collection: rColl, record_id: rRecord, backup_id: rBackup }); toast.success("Record restored from backup"); setRestoreOpen(false); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="backups-page">
      <h1 className="font-display text-2xl font-bold tracking-tight mb-1">Backups &amp; Recovery</h1>
      <p className="text-sm text-slate-500 mb-5">On-disk nightly backups · single-record restore · account-recovery procedures.</p>

      <ScaleHealthCard scale={scale} />
      <ServerCopiesCard />

      {(() => {
        const os = offsite, ph = photoHealth, bp = protection;
        const ago = (h) => h == null ? null : h < 1 ? "less than an hour ago" : h < 2 ? "1 hour ago" : h < 48 ? `${Math.round(h)} hours ago` : `${Math.round(h / 24)} days ago`;

        // 1) Off-site backup
        const bTone = !os ? "neutral" : !os.configured ? "warn" : os.last?.status === "failed" ? "bad" : os.stale ? "warn" : os.last?.status === "ok" ? "ok" : "warn";
        const bHead = !os ? "Checking…" : !os.configured ? "Off-site backup not set up" : os.last?.status === "failed" ? "Last off-site backup FAILED" : os.stale ? "Off-site backup overdue" : os.last?.status === "ok" ? "Off-site backup healthy ✓" : "No off-site backup yet";
        const bDetail = !os || !os.configured ? "Encrypted copy that survives losing the server." : os.last?.error ? `Error: ${os.last.error}` : `Encrypted copy stored off-site${os.last_success_at ? ` · last success ${(os.last_success_at || "").slice(0, 16).replace("T", " ")} UTC` : ""}.`;

        // 2) Scheduler / last-success age
        const sTone = !os || !os.configured ? "neutral" : os.stale ? "warn" : os.hours_since_success != null ? "ok" : "warn";
        const sHead = !os || !os.configured ? "Automatic backups: —" : os.hours_since_success != null ? `Last successful backup: ${ago(os.hours_since_success)}${os.stale ? " ⚠" : " ✓"}` : "No successful backup on record";
        // B-5: the NEXT fire, from the live scheduler. The old schedule was a stopwatch started at
        // boot, so "every N hours" was all anybody could honestly say. It is a wall clock now, so
        // the exact minute can be shown — and if it ever drifts, that is visible here first.
        const nextOffsite = (schedule?.jobs || []).find((j) => j.id === "offsite_backup")?.next_run_at;
        const sDetail = os && os.configured
          ? `Runs automatically every ${os.interval_hours} hour${os.interval_hours === 1 ? "" : "s"}`
            + (nextOffsite ? ` — next at ${nextOffsite.slice(11, 16)} UTC, on the clock (a restart can delay it by minutes, never by an hour).` : ".")
            + ` Warns if none in ${os.stale_after_hours}h. Counts manual runs too — the history below shows which were automatic.`
          : "Set up off-site backups to enable the schedule.";

        // 3) Photo storage — connectivity AND off-site backup freshness (photos are core data)
        const photoBackupBad = !!(os && (os.photo_backup_stale || os.photo_backup_unhealthy));
        const pending = os?.photos_pending || 0;
        const pTone = !ph ? "neutral" : !ph.reachable ? (ph.configured ? "bad" : "warn") : photoBackupBad ? "bad" : "ok";
        const pHead = !ph ? "Checking photo storage…"
          : !ph.reachable ? (ph.configured ? "Photo storage NOT reachable" : "Photo storage not set up")
          : photoBackupBad ? `Photos NOT backing up — ${pending} pending` : `Photos: connected & backed up ✓`;
        const pDetail = !ph ? ""
          : !ph.reachable ? (ph.configured ? `Error: ${ph.error} — new uploads at risk.` : "Uploads go to temporary disk and are wiped on redeploy.")
          : photoBackupBad ? `${pending} photo(s) not copied off-site${os?.photos_pending_oldest_hours != null ? ` (oldest ${os.photos_pending_oldest_hours}h)` : ""} — alerted.`
          : `${os?.photos_backed_up ?? 0} of ${(os?.photos_backed_up ?? 0) + pending} photos copied off-site${pending ? ` · ${pending} queued` : " — all of them"}. The bucket holds ${ph.object_count} files because each photo stores a full image AND a thumbnail.`;

        // 4) Bucket protection
        const lockOk = bp?.backup_bucket?.object_lock === "Enabled";
        const verOk = bp?.photo_bucket?.versioning === "Enabled";
        const prTone = !bp ? "neutral" : (lockOk && verOk) ? "ok" : "warn";
        const prHead = !bp ? "Checking protection…" : (lockOk && verOk) ? "Immutability & versioning: on ✓" : "Bucket protection needs attention";
        const prDetail = !bp ? "" : `Backups write-once lock: ${lockOk ? "on" : "off"} · Photos keep-all-versions: ${verOk ? "on" : "off"}.`;

        const tiles = [
          { key: "backup", tone: bTone, icon: Cloud, title: "Off-site backup", head: bHead, detail: bDetail },
          { key: "schedule", tone: sTone, icon: Clock, title: "Backup schedule", head: sHead, detail: sDetail },
          { key: "photos", tone: pTone, icon: Image, title: "Photo storage", head: pHead, detail: pDetail },
          { key: "protection", tone: prTone, icon: Lock, title: "Bucket protection", head: prHead, detail: prDetail },
        ];
        const rank = { bad: 3, warn: 2, neutral: 1, ok: 0 };
        const worst = tiles.reduce((w, t) => rank[t.tone] > rank[w] ? t.tone : w, "ok");
        const overall = worst === "bad" ? { t: TONES.bad, icon: XCircle, msg: "Problem detected — one or more items need immediate attention." }
          : worst === "warn" ? { t: TONES.warn, icon: AlertTriangle, msg: "Needs attention — everything's running, but review the amber item(s) below." }
          : worst === "neutral" ? { t: TONES.neutral, icon: Info, msg: "Loading status…" }
          : { t: TONES.ok, icon: ShieldCheck, msg: "All systems healthy — your data is backed up and protected." };

        return (
          <div data-testid="health-summary" className="mb-6">
            <div data-testid="health-overall" data-tone={worst} className={`rounded-xl border ${overall.t.wrap} px-4 py-3 flex items-center gap-3 mb-3`}>
              <span className={`w-9 h-9 rounded-lg flex items-center justify-center ${overall.t.chip}`}><overall.icon className="w-5 h-5" /></span>
              <div className={`font-semibold ${overall.t.text}`}>{overall.msg}</div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {tiles.map((t) => (
                <StatusTile key={t.key} testid={`health-tile-${t.key}`} tone={t.tone} icon={t.icon} title={t.title} headline={t.head} detail={t.detail} />
              ))}
            </div>
            <div data-testid="health-legend" className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-3 text-xs text-slate-500">
              <span className="font-mono uppercase tracking-wider text-[10px] text-slate-400">Color key:</span>
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500" /> Green = healthy</span>
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-amber-500" /> Amber = needs attention</span>
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#B01E28]" /> Red = problem</span>
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-slate-300" /> Grey = loading</span>
              <span className="ml-auto text-slate-400">Full detail below ↓</span>
            </div>
          </div>
        );
      })()}

      <div className="flex items-center gap-2 mb-3 mt-1">
        <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Full details</span>
        <div className="flex-1 h-px bg-slate-200" />
      </div>

      <div className="border border-amber-300 bg-amber-50 p-3 flex gap-2 mb-5 text-xs">
        <Info className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
        <span>On-disk backups protect against bad edits, accidental archives and corruption (restore-to-last-night + single-record recovery). They do <b>not</b> survive loss of the server/disk — that's what the <b>encrypted off-site backup</b> below is for.</span>
      </div>

      <ServerDiskCard disk={disk} onRefresh={loadDisk} />

      {/* OFF-SITE ENCRYPTED BACKUP */}
      <div data-testid="offsite-section" className="border border-slate-200 bg-white rounded-xl shadow-sm mb-5 overflow-hidden">
        <div className="bg-slate-100 px-4 py-2.5 font-mono text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><Cloud className="w-3.5 h-3.5 text-[#B01E28]" />Off-site encrypted backup (Backblaze B2)</div>
        <div className="p-4">
          {!offsite ? <div className="font-mono text-xs text-slate-400">LOADING…</div> : !offsite.configured ? (
            <div data-testid="offsite-not-configured" className="flex items-start gap-2 text-sm">
              <CloudOff className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <div className="font-medium text-slate-700">Not configured yet</div>
                <p className="text-xs text-slate-500 mt-0.5">Add your Backblaze B2 secrets to the backend environment (<code className="font-mono">B2_KEY_ID</code>, <code className="font-mono">B2_APPLICATION_KEY</code>, <code className="font-mono">B2_BUCKET_NAME</code>, <code className="font-mono">B2_ENDPOINT</code>, <code className="font-mono">BACKUP_ENCRYPTION_PASSPHRASE</code>) and restart. See <code className="font-mono">BACKUP_RESTORE.md</code>.</p>
              </div>
            </div>
          ) : (
            <>
              {offsite.stale ? (
                <div data-testid="backup-health-stale" className="flex items-start gap-2 border border-[#B01E28] bg-red-50 rounded-lg p-3 mb-3">
                  <AlertTriangle className="w-4 h-4 text-[#B01E28] shrink-0 mt-0.5" />
                  <div className="text-xs text-[#B01E28]">
                    <b>Off-site backup is overdue.</b>{" "}
                    {offsite.hours_since_success != null
                      ? `Last successful backup was ${offsite.hours_since_success}h ago`
                      : "No successful off-site backup on record"}
                    {" "}(alert threshold: {offsite.stale_after_hours}h). Your data-loss exposure is elevated — run a backup now and check the error below.
                  </div>
                </div>
              ) : (
                <div data-testid="backup-health-ok" className="flex items-center gap-2 text-xs text-emerald-700 mb-3">
                  <CheckCircle2 className="w-4 h-4" />
                  Healthy — last successful off-site backup {offsite.hours_since_success != null ? `${offsite.hours_since_success}h ago` : "just now"} (alerts if none in {offsite.stale_after_hours}h).
                </div>
              )}
              <div className="flex flex-wrap items-center gap-3 mb-3">
                {(() => {
                  const ok = offsite.last?.status === "ok";
                  const failed = offsite.last?.status === "failed";
                  return (
                    <span data-testid="offsite-status" className={`inline-flex items-center gap-1.5 text-sm font-medium ${ok ? "text-emerald-700" : failed ? "text-[#B01E28]" : "text-slate-600"}`}>
                      {ok ? <CheckCircle2 className="w-4 h-4" /> : failed ? <XCircle className="w-4 h-4" /> : <Cloud className="w-4 h-4" />}
                      {offsite.last ? `Last backup: ${(offsite.last.created_at || "").slice(0, 16).replace("T", " ")} UTC · ${(offsite.last.status || "").toUpperCase()}` : "No off-site backup yet"}
                    </span>
                  );
                })()}
                <Button data-testid="run-offsite" onClick={runOffsite} disabled={offRunning} className="rounded-lg bg-[#E0781A] hover:bg-[#C76714] text-white h-9 ml-auto">{offRunning ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Cloud className="w-4 h-4 mr-1" />}Run off-site backup now</Button>
                <Button data-testid="download-latest-backup" onClick={downloadLatest} disabled={dlBusy} variant="outline" className="rounded-lg border-slate-300 h-9">{dlBusy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <DatabaseBackup className="w-4 h-4 mr-1" />}Download my copy</Button>
              </div>
              {offsite.last?.error && <div className="text-xs text-[#B01E28] mb-2">Error: {offsite.last.error}</div>}
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-xs font-mono text-slate-500">
                <div>Bucket: <span className="text-slate-700">{offsite.bucket}</span></div>
                <div className="flex items-center gap-1"><Lock className="w-3 h-3" />Encrypted: AES-256 + HMAC (client-side)</div>
                <div>Schedule: every {offsite.interval_hours}h</div>
                <div>Retention: {offsite.retention} DB archives</div>
                <div data-testid="photos-backed-up">Photos backed up: <span className="text-emerald-700">{offsite.photos_backed_up ?? 0}</span>{offsite.photos_pending ? <span className="text-amber-600"> · {offsite.photos_pending} pending</span> : null}</div>
                {offsite.last_success_at && <div>Last success: <span className="text-emerald-700">{(offsite.last_success_at || "").slice(0, 16).replace("T", " ")} UTC</span></div>}
              </div>
              {offsite.heartbeat_configured ? (
                <div data-testid="heartbeat-armed" className="mt-3 flex items-start gap-2 rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-xs text-emerald-800">
                  <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5" />
                  <div>
                    <b>Outside witness armed.</b> Every backup pings an external monitor, and a failed run pings it immediately. If this app stops running, the monitor stops hearing from it and emails you — no app process required.
                    {offsite.heartbeat_last && (
                      <div data-testid="heartbeat-last" className="font-mono text-[11px] mt-1 text-emerald-700">
                        last signal: {offsite.heartbeat_last.signal?.toUpperCase()} · {offsite.heartbeat_last.state}
                        {offsite.heartbeat_last.at ? ` · ${String(offsite.heartbeat_last.at).slice(0, 16).replace("T", " ")} UTC` : ""}
                        {offsite.heartbeat_last.state === "unreachable" && <span className="text-[#B01E28]"> — the monitor could not be reached ({offsite.heartbeat_last.error}); the backup itself was unaffected</span>}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div data-testid="heartbeat-not-armed" className="mt-3 flex items-start gap-2 rounded-lg border border-amber-400 bg-amber-50 p-3 text-xs text-amber-900">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <div>
                    <b>No outside witness.</b> The staleness watchdog and the failure email both run INSIDE this app. If this app stops running, they stop with it and nothing tells you the backups have gone quiet. Set <code className="font-mono">{offsite.heartbeat_env_key}</code> in the backend environment to the ping URL of an external monitor (a free Healthchecks.io check is enough) and restart. The URL is a credential — it is never displayed here.
                  </div>
                </div>
              )}
              {offsite.history?.length > 0 && (
                <div className="mt-3 border-t border-slate-100 pt-2 max-h-40 overflow-y-auto">
                  {offsite.history.slice(0, 12).map((h) => (
                    <div key={h.backup_id} data-testid={`offsite-row-${h.backup_id}`} className="flex items-center gap-2 py-1 text-[11px] font-mono">
                      <Badge className={`rounded text-[8px] uppercase text-white ${h.status === "ok" ? "bg-emerald-600" : h.status === "failed" ? "bg-[#B01E28]" : "bg-slate-400"}`}>{h.status}</Badge>
                      <span className="text-slate-600">{h.backup_id}</span>
                      {h.size_bytes ? <span className="text-slate-400">{(h.size_bytes / 1e6).toFixed(2)} MB</span> : null}
                      {h.triggered_by === "scheduler" ? (
                        <Badge data-testid={`offsite-trigger-${h.backup_id}`} className="rounded text-[8px] uppercase bg-blue-600 text-white ml-auto">Auto</Badge>
                      ) : (
                        <span data-testid={`offsite-trigger-${h.backup_id}`} className="ml-auto flex items-center gap-1">
                          <Badge className="rounded text-[8px] uppercase bg-[#E0781A] text-white">Manual</Badge>
                          <span className="text-slate-400 normal-case">{h.triggered_by}</span>
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {protection && (
        <div data-testid="bucket-protection-card" className="border border-slate-200 bg-white rounded-xl shadow-sm mb-5 overflow-hidden">
          <div className="bg-slate-100 px-4 py-2.5 font-mono text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><Lock className="w-3.5 h-3.5 text-[#B01E28]" />Bucket protection (immutability &amp; versioning)</div>
          <div className="p-4 grid sm:grid-cols-2 gap-3">
            {[["Backup bucket", protection.backup_bucket, "object_lock", "Object Lock (write-once)"],
              ["Photo bucket", protection.photo_bucket, "versioning", "Versioning (keep all versions)"]].map(([title, b, keyf, want]) => {
              const val = b?.[keyf];
              const good = val === "Enabled";
              return (
                <div key={title} data-testid={`protection-${keyf}`} className="border border-slate-200 rounded-lg p-3">
                  <div className="text-xs font-semibold text-slate-700 mb-1">{title}</div>
                  <div className="font-mono text-[11px] text-slate-500 truncate mb-2">{b?.bucket || "—"}</div>
                  <div className="flex items-center gap-1.5 text-sm">
                    {good ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <AlertTriangle className="w-4 h-4 text-amber-600" />}
                    <span className={good ? "text-emerald-700" : "text-amber-700"}>{want}: <b>{val || "unknown"}</b></span>
                  </div>
                  {keyf === "object_lock" && b?.object_lock_days ? <div className="text-[11px] text-slate-400 font-mono mt-1">retention ~{b.object_lock_days} days</div> : null}
                  {!good && <div className="text-[11px] text-amber-700 mt-1">Not protected — enable in the Backblaze console (steps in the guide).</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}


      <div data-testid="restore-drill-card" className="border-2 border-emerald-300 bg-emerald-50 rounded-xl shadow-sm mb-5 overflow-hidden">
        <div className="bg-emerald-100 px-4 py-2.5 font-mono text-[10px] uppercase tracking-wider text-emerald-800 flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" />Verify backup (safe · read-only · no DB writes)</div>
        <div className="p-4">
          <p className="text-xs text-slate-600 mb-3">Proves your latest off-site backup is <b>complete, decryptable, and contains your real data</b>. Downloads + decrypts the real archive and checks it on-disk: counts your donors + every collection, confirms each collection's data fully decodes, cross-checks counts against the backup manifest, and opens a photo. It <b>writes nothing and touches no database</b> — safe on your live-DB-only account. (This verifies the archive; it does not test-restore into a live database — see the runbook for the occasional full offline restore.)</p>
          <Button data-testid="run-restore-drill" onClick={runDrill} disabled={drillBusy} className="rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white h-9">
            {drillBusy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <ShieldCheck className="w-4 h-4 mr-1" />}{drillBusy ? "Verifying…" : "Verify latest backup"}
          </Button>
          {offsite?.last_verify && (
            <div data-testid="last-verify-indicator" className="mt-3 flex items-start gap-2 text-xs">
              {offsite.last_verify.ok
                ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                : <XCircle className="w-4 h-4 text-[#B01E28] shrink-0 mt-0.5" />}
              <span className="text-slate-600">Last verified <b className="text-slate-900">{String(offsite.last_verify.at).slice(0, 16).replace("T", " ")} UTC</b> — <b className={offsite.last_verify.ok ? "text-emerald-700" : "text-[#B01E28]"}>{offsite.last_verify.ok ? "PASS" : "FAIL"}</b>{offsite.last_verify.trigger?.startsWith("scheduled") ? " (auto)" : ""}
                {offsite.last_verify.archive_age_hours != null && (
                  <span data-testid="last-verify-archive-age" className="text-slate-500"> · archive was <b className={offsite.last_verify.archive_too_old ? "text-[#B01E28]" : "text-slate-700"}>{(offsite.last_verify.archive_age_hours / 24).toFixed(1)} days</b> old</span>
                )}
                {offsite.last_verify.archive_is_this_app === false && (
                  <span data-testid="last-verify-foreign" className="text-[#B01E28] font-semibold"> · that archive belonged to ANOTHER app</span>
                )}
              </span>
            </div>
          )}
          <div className="mt-3 flex items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2">
            <div className="text-xs text-slate-600 pr-3">
              <div className="font-semibold text-slate-800">Monthly auto-verify</div>
              Runs this check on the 1st of each month and emails a PASS/FAIL summary to your critical-alert recipients (a FAIL alerts loudly, like a backup failure).
            </div>
            <Switch data-testid="auto-verify-toggle" checked={!!offsite?.auto_verify_enabled} disabled={verifSchedBusy} onCheckedChange={toggleVerifySchedule} />
          </div>
          <div className="mt-2 flex items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2">
            <div className="text-xs text-slate-600 pr-3">
              <div className="font-semibold text-slate-800">Warn if not verified within</div>
              The owner Home shows an amber "backups not proven restorable" warning once the last passing verify is older than this many days (or never / failed).
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <input data-testid="verify-stale-days-input" type="number" min={1} max={90}
                key={offsite?.verify_stale_days ?? "d"}
                defaultValue={offsite?.verify_stale_days ?? 7}
                onBlur={(e) => saveVerifyStaleDays(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") saveVerifyStaleDays(e.target.value); }}
                disabled={staleDaysBusy}
                className="w-16 rounded-md border border-slate-300 px-2 py-1 text-sm text-right" />
              <span className="text-xs text-slate-500">days</span>
            </div>
          </div>
          <div className="mt-2 flex items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2">
            <div className="text-xs text-slate-600 pr-3">
              <div className="font-semibold text-slate-800">Warn if a backup hasn't LANDED within</div>
              The owner Home shows "off-site backups NOT landing" once the last successful automatic off-site backup is older than this many hours (or none ever). Separate fact from verifying — reflects that new data has stopped being copied off-site.
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <input data-testid="landing-stale-hours-input" type="number" min={1} max={168}
                key={offsite?.landing_stale_hours ?? "h"}
                defaultValue={offsite?.landing_stale_hours ?? 26}
                onBlur={(e) => saveLandingStaleHours(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") saveLandingStaleHours(e.target.value); }}
                disabled={landingHoursBusy}
                className="w-16 rounded-md border border-slate-300 px-2 py-1 text-sm text-right" />
              <span className="text-xs text-slate-500">hours</span>
            </div>
          </div>
          {drill && (
            <div data-testid="drill-result" className={`mt-4 rounded-lg border p-3 ${drill.ok ? "border-emerald-300 bg-white" : "border-red-300 bg-red-50"}`}>
              <div className={`flex items-center gap-2 font-semibold mb-2 ${drill.ok ? "text-emerald-700" : "text-[#B01E28]"}`}>
                {drill.ok ? <CheckCircle2 className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
                {drill.ok
                  ? "Backup VERIFIED — your data, recent, complete & decryptable"
                  : drill.refusals?.length
                    ? "VERIFICATION REFUSED — this does NOT prove your backups"
                    : "Backup verification FAILED"}
              </div>
              {drill.refusals?.length > 0 && (
                <ul data-testid="drill-refusals" className="mb-2 space-y-1">
                  {drill.refusals.map((r, i) => (
                    <li key={i} data-testid={`drill-refusal-${i}`} className="flex items-start gap-2 text-xs text-[#B01E28]">
                      <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />{r}
                    </li>
                  ))}
                </ul>
              )}
              {(drill.archive_age_hours != null || drill.archive_db_name) && (
                <div data-testid="drill-archive-facts" className="mb-2 grid sm:grid-cols-2 gap-x-6 gap-y-1 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-mono">
                  <div data-testid="drill-archive-age">Archive age: <b className={drill.archive_too_old ? "text-[#B01E28]" : "text-slate-900"}>{drill.archive_age_hours != null ? `${(drill.archive_age_hours / 24).toFixed(1)} days` : "unknown"}</b>{drill.max_age_hours ? <span className="text-slate-400"> · must be under {drill.max_age_hours}h</span> : null}</div>
                  <div data-testid="drill-archive-owner">Whose data: <b className={drill.archive_is_this_app ? "text-emerald-700" : "text-[#B01E28]"}>{drill.archive_is_this_app ? "this app ✓" : "ANOTHER app's archive ✗"}</b></div>
                </div>
              )}
              {drill.collections ? (
                <>
                  {drill.archive_is_this_app === false && (
                    <p data-testid="drill-foreign-caption" className="text-[11px] text-[#B01E28] mb-1">
                      The figures below describe <b>that other archive</b>, not this app's data. They prove nothing about your yard.
                    </p>
                  )}
                  <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-xs font-mono text-slate-600">
                    <div data-testid="drill-donors">Donors in backup: <b className="text-slate-900">{drill.donors}</b></div>
                    <div data-testid="drill-collections">Collections readable: <b className="text-slate-900">{drill.collections}</b></div>
                    <div>Parts in backup: <b className="text-slate-900">{drill.parts}</b></div>
                    <div>Total documents: <b className="text-slate-900">{drill.total_docs}</b></div>
                    <div className="sm:col-span-2">BSON integrity: {drill.undecodable_collections?.length ? <span className="text-[#B01E28]">✗ {drill.undecodable_collections.length} collection(s) did not decode</span> : <span className="text-emerald-700">✓ every collection decoded</span>} · manifest cross-check: <b className={drill.manifest_cross_check === "passed" ? "text-emerald-700" : drill.manifest_cross_check === "MISMATCH" ? "text-[#B01E28]" : "text-slate-500"}>{drill.manifest_cross_check}</b></div>
                    <div data-testid="drill-photo" className="sm:col-span-2">Photo open test: {drill.photo?.tested ? (drill.photo.openable ? <span className="text-emerald-700">✓ opened a real {drill.photo.format || "image"} ({drill.photo.filename})</span> : <span className="text-[#B01E28]">✗ found a file but it did not open</span>) : <span className="text-slate-400">no off-site photo to test yet ({drill.photo?.note})</span>}</div>
                    <div className="sm:col-span-2 text-slate-400 truncate">Verified archive: {drill.verified_archive} ({drill.bucket}){drill.archive_created_at ? ` · created ${String(drill.archive_created_at).slice(0,16).replace("T"," ")}` : ""}</div>
                    <div className="sm:col-span-2 text-emerald-700">No database writes ✓ · live DB untouched ✓ · temp files removed ✓</div>
                  </div>
                </>
              ) : (
                <div className="text-xs text-[#B01E28] font-mono">{drill.error}</div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="flex gap-2 mb-3">
        <Button data-testid="run-backup" onClick={runBackup} disabled={running} className="rounded-lg bg-[#E0781A] hover:bg-[#C76714] text-white">{running ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Play className="w-4 h-4 mr-1" />}Run backup now</Button>
        <Button data-testid="open-restore" variant="outline" className="rounded-lg border-slate-200" onClick={() => setRestoreOpen(true)}><RotateCcw className="w-4 h-4 mr-1" />Restore a record</Button>
      </div>

      <div className="border border-slate-200 bg-white">
        <div className="bg-slate-100 px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><DatabaseBackup className="w-3.5 h-3.5 text-[#B01E28]" />Backups (nightly 03:00 UTC + on-demand)</div>
        {loading ? <div className="p-8 text-center font-mono text-xs text-slate-400">LOADING…</div>
          : info.backups.length === 0 ? <div className="p-8 text-center font-mono text-xs text-slate-400">NONE YET — run one above</div>
          : info.backups.map((b) => (
            <div key={b.backup_id} data-testid={`backup-${b.backup_id}`} className="flex items-center gap-2 px-3 py-2 border-t border-slate-100 text-sm">
              <span className="font-mono text-xs font-bold">{b.backup_id}</span>
              <span className="flex-1 text-slate-500 font-mono text-[11px]">{b.created_at}</span>
              <Badge variant="outline" className="rounded-lg text-[9px]">{b.triggered_by}</Badge>
              <span className="font-mono text-[10px] text-slate-400">{Object.values(b.counts || {}).reduce((a, c) => a + c, 0)} docs</span>
            </div>
          ))}
      </div>

      {canMaintainPhotos && (
        <div data-testid="photo-storage-health" className={`border mt-5 p-4 rounded-xl ${photoHealth ? (photoHealth.reachable ? "border-green-300 bg-green-50" : photoHealth.configured ? "border-red-300 bg-red-50" : "border-amber-300 bg-amber-50") : "border-slate-200 bg-white"}`}>
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              {photoHealth?.reachable ? <Cloud className="w-5 h-5 text-green-600" /> : <CloudOff className="w-5 h-5 text-slate-400" />}
              <div>
                <div className="font-display font-bold text-sm text-slate-800">
                  {!photoHealth ? "Checking photo storage…"
                    : photoHealth.reachable ? "Photo storage: B2 ✓ — persistent"
                    : photoHealth.configured ? "Photo storage: B2 NOT reachable"
                    : "Photo storage: NOT configured (local disk — NOT persistent!)"}
                </div>
                <div className="text-xs font-mono text-slate-500">
                  {photoHealth?.reachable && <>bucket {photoHealth.bucket} · {photoHealth.object_count} object{photoHealth.object_count === 1 ? "" : "s"} · survives redeploys</>}
                  {photoHealth && !photoHealth.reachable && photoHealth.configured && <>bucket {photoHealth.bucket} · error: {photoHealth.error} — new uploads at risk, check B2 keys</>}
                  {photoHealth && !photoHealth.configured && <>set B2_PHOTOS_* secrets — uploads currently go to ephemeral disk and will be wiped on redeploy</>}
                </div>
                {photoHealth?.heif && (
                  <div data-testid="heif-health" className="text-xs font-mono mt-1">
                    {photoHealth.heif.can_decode
                      ? <span className="text-emerald-700">iPhone HEIC support: ON ✓ (pillow_heif {photoHealth.heif.version})</span>
                      : <span className="text-[#B01E28]">iPhone HEIC support: OFF ✗ — HEIC uploads will be rejected{photoHealth.heif.version ? ` (pillow_heif ${photoHealth.heif.version} loaded but cannot decode)` : " (pillow-heif not installed)"}</span>}
                  </div>
                )}
              </div>
            </div>
            <Button data-testid="photo-health-refresh" onClick={() => { setPhotoHealth(null); loadPhotoHealth(); }} size="sm" variant="outline" className="rounded-lg border-slate-300 h-8 shrink-0"><RotateCcw className="w-3.5 h-3.5" /></Button>
          </div>
        </div>
      )}

      {canMaintainPhotos && (
        <div data-testid="photo-maintenance-section" className="border border-slate-200 bg-white mt-5 p-4 rounded-xl">
          <div className="font-mono text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1.5 mb-2"><ImageOff className="w-3.5 h-3.5 text-[#B01E28]" />Photo maintenance — clear missing-file blanks</div>
          <p className="text-xs text-slate-600 mb-3">Finds photo records whose image files are missing (e.g. lost to a redeploy before persistent storage) and <b>archives</b> them so blanks stop showing. Archived, never deleted — retained, logged, and restorable.</p>
          <div className="flex flex-wrap items-center gap-2">
            <Button data-testid="orphan-scan-btn" onClick={() => scanOrphans(true)} disabled={orphanBusy} size="sm" variant="outline" className="rounded-lg border-slate-300 h-8">
              {orphanBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Info className="w-3.5 h-3.5 mr-1" />}Scan (dry-run)
            </Button>
            <Button data-testid="orphan-archive-btn" onClick={() => scanOrphans(false)} disabled={orphanBusy} size="sm" className="rounded-lg bg-[#B01E28] hover:bg-[#8F1820] text-white h-8">
              {orphanBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <RotateCcw className="w-3.5 h-3.5 mr-1" />}Archive orphaned photos
            </Button>
            {orphanResult && (
              <span data-testid="orphan-result" className="text-xs font-mono text-slate-500">
                {orphanResult.dry_run ? "found" : "archived"} {orphanResult.archived} · scanned {orphanResult.scanned} (donors {orphanResult.by_collection.donors}, parts {orphanResult.by_collection.parts}, lots {orphanResult.by_collection.capture_batches})
              </span>
            )}
          </div>
        </div>
      )}

      <div className="border border-slate-200 bg-white mt-5 p-4">
        <div className="font-mono text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1.5 mb-2"><ShieldAlert className="w-3.5 h-3.5 text-[#B01E28]" />Account recovery procedure (no single point of lockout)</div>
        <ol className="text-xs text-slate-600 list-decimal pl-5 space-y-1">
          <li><b>Backup codes:</b> every user can generate one-time 2FA backup codes on the <b>Recovery</b> page; a code stands in for the authenticator code at login.</li>
          <li><b>Second admin:</b> always keep at least two Admin accounts. Create a second Admin under <b>Users</b>.</li>
          <li><b>Admin lockout:</b> a second Admin opens <b>Users</b> → the locked-out admin → <b>Reset 2FA</b>. The target re-enrolls a fresh authenticator at next login; old sessions are revoked immediately.</li>
          <li><b>Bad data:</b> use <b>Restore a record</b> above to recover a single document from last night's backup.</li>
        </ol>
      </div>

      <Dialog open={restoreOpen} onOpenChange={setRestoreOpen}>
        <DialogContent className="rounded-lg border-slate-200 sm:max-w-md" data-testid="restore-dialog">
          <DialogHeader><DialogTitle className="font-display">Restore a single record</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Select value={rBackup} onValueChange={(v) => { setRBackup(v); setRRecord(""); loadRecords(v, rColl); }}>
              <SelectTrigger data-testid="restore-backup" className="rounded-lg border-slate-200"><SelectValue placeholder="Select backup" /></SelectTrigger>
              <SelectContent className="rounded-lg max-h-60">{info.backups.map((b) => <SelectItem key={b.backup_id} value={b.backup_id}>{b.backup_id}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={rColl} onValueChange={(v) => { setRColl(v); setRRecord(""); loadRecords(rBackup, v); }}>
              <SelectTrigger data-testid="restore-collection" className="rounded-lg border-slate-200"><SelectValue placeholder="Select collection" /></SelectTrigger>
              <SelectContent className="rounded-lg max-h-60">{info.collections.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={rRecord} onValueChange={setRRecord}>
              <SelectTrigger data-testid="restore-record" className="rounded-lg border-slate-200"><SelectValue placeholder="Select record" /></SelectTrigger>
              <SelectContent className="rounded-lg max-h-60">{rRecords.map((r) => <SelectItem key={r.record_id} value={r.record_id}>{r.label || r.record_id}</SelectItem>)}</SelectContent>
            </Select>
            <Button data-testid="confirm-restore" onClick={doRestore} className="rounded-lg bg-[#E0781A] hover:bg-[#C76714] text-white w-full">Restore this record</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
